import { Controller } from "@hotwired/stimulus"
import axios from "axios"

// Floating System data-chat widget: a bottom-left bubble that toggles a
// drawer. The conversation lives server-side (one per admin, resolved from
// current_admin); history is fetched the first time the drawer is opened.
// CSRF is handled globally by axios.defaults (see entrypoints/application.js).
export default class extends Controller {
  static targets = ["drawer", "messages", "input", "submit", "pending", "error", "placeholder"]
  static values = { historyUrl: String, askUrl: String, clearUrl: String }

  declare readonly drawerTarget: HTMLElement
  declare readonly messagesTarget: HTMLElement
  declare readonly inputTarget: HTMLTextAreaElement
  declare readonly submitTarget: HTMLButtonElement
  declare readonly pendingTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly hasPlaceholderTarget: boolean
  declare readonly placeholderTarget: HTMLElement
  declare historyUrlValue: string
  declare askUrlValue: string
  declare clearUrlValue: string

  private historyLoaded = false

  connect(): void {
    this.inputTarget.addEventListener("keydown", this.onKeydown)
  }

  disconnect(): void {
    this.inputTarget.removeEventListener("keydown", this.onKeydown)
  }

  onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void this.submit()
    }
  }

  toggle(): void {
    const opened = !this.drawerTarget.classList.toggle("hidden")
    if (!opened) return

    this.inputTarget.focus()
    if (!this.historyLoaded) void this.loadHistory()
  }

  async submit(event?: Event): Promise<void> {
    event?.preventDefault()

    const question = this.inputTarget.value.trim()
    if (!question || this.submitTarget.disabled) return

    this.setBusy(true)
    this.hideError()

    try {
      const { data } = await axios.post(this.askUrlValue, { question })
      this.removePlaceholder()
      this.messagesTarget.insertAdjacentHTML("beforeend", data.html)
      this.inputTarget.value = ""
      this.scrollToBottom()
    } catch (error) {
      this.showError(error)
    } finally {
      this.setBusy(false)
      this.inputTarget.focus()
    }
  }

  async clear(): Promise<void> {
    if (!window.confirm("Effacer la conversation ?")) return

    try {
      await axios.delete(this.clearUrlValue)
      this.messagesTarget.innerHTML = ""
      this.hideError()
    } catch (error) {
      this.showError(error)
    }
  }

  private async loadHistory(): Promise<void> {
    this.historyLoaded = true

    try {
      const { data } = await axios.get(this.historyUrlValue)
      if (data.html && data.html.trim()) {
        this.removePlaceholder()
        this.messagesTarget.insertAdjacentHTML("beforeend", data.html)
        this.scrollToBottom()
      }
    } catch (error) {
      this.historyLoaded = false
      this.showError(error)
    }
  }

  private removePlaceholder(): void {
    if (this.hasPlaceholderTarget) this.placeholderTarget.remove()
  }

  private setBusy(busy: boolean): void {
    this.submitTarget.disabled = busy
    this.pendingTarget.classList.toggle("hidden", !busy)
  }

  private hideError(): void {
    this.errorTarget.classList.add("hidden")
  }

  private showError(error: unknown): void {
    let message = "Une erreur inattendue est survenue."
    if (axios.isAxiosError(error)) {
      message = error.response?.data?.error || error.message
    } else if (error instanceof Error) {
      message = error.message
    }
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  private scrollToBottom(): void {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}
